import styled from '@emotion/styled';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import TimelineIcon from '@mui/icons-material/Timeline';
import type { Theme } from '@trinity/chat-components/types/theme';
import { shouldForwardProp } from '@trinity/chat-components/utils/emotion';

type WrapProps = {
  isDisabled?: boolean;
};

type IconProps = {
  isDisabled?: boolean;
};

export const Root = styled.button`
  display: block;
`;

export const Wrap = styled('div', {
  shouldForwardProp: shouldForwardProp('isFullPageChat', 'isDisabled'),
})<WrapProps>`
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => props.theme.palette.text.primary};
  cursor: ${(props) => (props.isDisabled ? 'default' : 'pointer')};
  border-radius: 2px;
  font-size: 14px;
  font-family: ${(props) => props.theme.fonts.regular};
  padding: 6px 8px;
  transition: background-color 0.2s;
  text-align: left;
`;

const defaultIconStyles = `
  height: 16px;
  width: 16px;
  font-size: 18px;
  margin-right: 4px;
`;

export const TextStyledIcon = styled(ArrowForwardIcon, {
  shouldForwardProp: shouldForwardProp('isDisabled'),
})<IconProps>(defaultIconStyles);

export const AnalysisStyledIcon = styled(AutoAwesomeIcon, {
  shouldForwardProp: shouldForwardProp('isDisabled'),
})<IconProps>(defaultIconStyles);

export const TimelineStyledIcon = styled(TimelineIcon, {
  shouldForwardProp: shouldForwardProp('isDisabled'),
})<IconProps>(defaultIconStyles);

export const CancelStyledIcon = styled(CloseIcon, {
  shouldForwardProp: shouldForwardProp('isDisabled'),
})<IconProps>(defaultIconStyles);

export const SearchStyledIcon = styled(SearchIcon, {
  shouldForwardProp: shouldForwardProp('isDisabled'),
})<IconProps>(defaultIconStyles);

export const DownloadStyledIcon = styled(DownloadIcon, {
  shouldForwardProp: shouldForwardProp('isDisabled'),
})<IconProps>(defaultIconStyles);

export const LocationStyledIcon = styled(LocationOnIcon, {
  shouldForwardProp: shouldForwardProp('isDisabled'),
})<IconProps>(defaultIconStyles);

export const Colors = (theme: Theme.Theme) => {
  const { gray, green, purple, wl: wlBase } = theme.palette;

  const wl = wlBase?.prompt;

  return {
    Text: {
      enabled: {
        backgroundColor: wl?.main || gray.light5,
        hover: {
          backgroundColor: wl?.dark1 || gray.light6,
        },
      },
      disabled: {
        backgroundColor: theme.palette.gray.light2,
        hover: {
          backgroundColor: theme.palette.gray.light2,
        },
      },
    },
    TextIcon: {
      enabled: {
        color: wl?.icon || purple.light8,
      },
      disabled: {
        color: theme.palette.gray.main,
      },
    },
    Analysis: {
      enabled: {
        backgroundColor: wl?.main || green.light3,
        hover: {
          backgroundColor: wl?.dark1 || green.light2,
        },
      },
      disabled: {
        backgroundColor: theme.palette.gray.light2,
        hover: {
          backgroundColor: theme.palette.gray.light2,
        },
      },
    },
    AnalysisIcon: {
      enabled: {
        color: wl?.icon || green.dark2,
      },
      disabled: {
        color: theme.palette.gray.main,
      },
    },
    TimelineIcon: {
      enabled: {
        color: wl?.icon || purple.light8,
      },
      disabled: {
        color: theme.palette.gray.main,
      },
    },
    DefaultIcon: {
      enabled: {
        color: wl?.icon || green.dark2,
      },
      disabled: {
        color: theme.palette.gray.main,
      },
    },
    Cancel: {
      enabled: {
        backgroundColor: wl?.main || gray.light1,
        hover: {
          backgroundColor: wl?.dark1 || gray.light1,
        },
      },
      disabled: {
        backgroundColor: theme.palette.gray.light2,
        hover: {
          backgroundColor: theme.palette.gray.light2,
        },
      },
    },
    CancelIcon: {
      enabled: {
        color: wl?.icon || theme.palette.black.main,
      },
      disabled: {
        color: theme.palette.gray.main,
      },
    },
  };
};
